import { EmoCart, EmoEye, EmoLocation, EmoNeutral, EmoPin } from "@assets"
import { Head, router } from "@inertiajs/react"
import { Users } from "lucide-react"
import { useEffect, useState } from "react"

import FeedItem, {
  type FeedItem as FeedItemData,
} from "@/components/feed/feed-item"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"

interface Event {
  id: number
  type: "event"
  source_id: number
  title: string
  slug: string
  start_date: string
  description?: string
  image_url?: string
  attendees_count: number
  created_at: string
  is_free: boolean
  price?: number
  venue_name?: string
  organizer: {
    id: number
    name: string
    verified?: boolean
  }
  event_attendees: {
    id: number
    status: string
  }[]
  neighborhood?: {
    id: number
    name: string
  }
  city?: {
    id: number
    name: string
  }
}

interface Business {
  id: number
  type: "business"
  source_id: number
  name: string
  business_name: string
  business_type: string
  category: string
  description?: string
  image_url?: string
  rating: number
  total_reviews?: number
  created_at: string
  verified?: boolean
  owner?: {
    id: number
    name: string
    verified?: boolean
  }
  neighborhood?: {
    id: number
    name: string
  }
  city?: {
    id: number
    name: string
  }
  images?: string[]
}

interface ForumPost {
  id: number
  type: "forum_post"
  source_id: number
  title: string
  slug: string
  content: string
  content_preview: string
  answers_count: number
  created_at: string
  author: {
    id: number
    name: string
    verified?: boolean
  }
  category: {
    id: number
    name: string
  }
  forum_answers: {
    id: number
  }[]
  forum_votes: {
    vote_type: string
  }[]
  view_count: number
}

interface Neighborhood {
  id: number
  name: string
  slug: string
}

interface User {
  id: number
  name: string
  email: string
  verified: boolean
  created_at: string
  current_neighborhood?: {
    id: number
    name: string
    slug: string
  }
  current_city?: {
    id: number
    name: string
    slug: string
  }
}

interface MiBarrioProps {
  current_user: User
  neighborhood: Neighborhood
  events: {
    items: Event[]
    total: number
  }
  businesses: {
    items: Business[]
    total: number
  }
  forum_posts: {
    items: ForumPost[]
    total: number
  }
  argentinos_count: number
  can_change_neighborhood: boolean
  active_tab?: string
  pagination: {
    current_offset: number
    limit: number
    total: number
    has_more: boolean
  }
}

export default function MiBarrioIndex({
  neighborhood,
  events,
  businesses,
  forum_posts,
  argentinos_count,
  active_tab = "eventos",
  pagination,
}: MiBarrioProps) {
  const [activeTab, setActiveTab] = useState<"eventos" | "negocios" | "foro">(
    active_tab as "eventos" | "negocios" | "foro",
  )
  const [allEvents, setAllEvents] = useState(events.items)
  const [allBusinesses, setAllBusinesses] = useState(businesses.items)
  const [allForumPosts, setAllForumPosts] = useState(forum_posts.items)
  const [loadingMore, setLoadingMore] = useState(false)

  // Update items when data changes (similar to Feed)
  useEffect(() => {
    if (activeTab === "eventos" && events.items.length > 0) {
      setAllEvents((prevItems) => {
        if (pagination.current_offset === 0) {
          return events.items
        } else {
          const newItems = events.items.filter(
            (newItem) =>
              !prevItems.some((existingItem) => existingItem.id === newItem.id),
          )
          return newItems.length > 0 ? [...prevItems, ...newItems] : prevItems
        }
      })
    } else if (pagination.current_offset === 0) {
      setAllEvents(events.items)
    }
  }, [events.items, pagination.current_offset, activeTab])

  useEffect(() => {
    if (activeTab === "negocios" && businesses.items.length > 0) {
      setAllBusinesses((prevItems) => {
        if (pagination.current_offset === 0) {
          return businesses.items
        } else {
          const newItems = businesses.items.filter(
            (newItem) =>
              !prevItems.some((existingItem) => existingItem.id === newItem.id),
          )
          return newItems.length > 0 ? [...prevItems, ...newItems] : prevItems
        }
      })
    } else if (pagination.current_offset === 0) {
      setAllBusinesses(businesses.items)
    }
  }, [businesses.items, pagination.current_offset, activeTab])

  useEffect(() => {
    if (activeTab === "foro" && forum_posts.items.length > 0) {
      setAllForumPosts((prevItems) => {
        if (pagination.current_offset === 0) {
          return forum_posts.items
        } else {
          const newItems = forum_posts.items.filter(
            (newItem) =>
              !prevItems.some((existingItem) => existingItem.id === newItem.id),
          )
          return newItems.length > 0 ? [...prevItems, ...newItems] : prevItems
        }
      })
    } else if (pagination.current_offset === 0) {
      setAllForumPosts(forum_posts.items)
    }
  }, [forum_posts.items, pagination.current_offset, activeTab])

  const tabs = [
    {
      key: "eventos" as const,
      label: "Eventos",
      icon: EmoPin,
      count: events.total,
    },
    {
      key: "negocios" as const,
      label: "Negocios",
      icon: EmoCart,
      count: businesses.total,
    },
    {
      key: "foro" as const,
      label: "Foro",
      icon: EmoEye,
      count: forum_posts.total,
    },
  ]

  const handleTabChange = (tabKey: "eventos" | "negocios" | "foro") => {
    setActiveTab(tabKey)
    setLoadingMore(false)
    router.get(
      "/mi-barrio",
      {
        tab: tabKey,
        offset: 0,
      },
      {
        preserveState: true,
        preserveScroll: true,
      },
    )
  }

  const handleLoadMore = () => {
    if (loadingMore || !pagination.has_more) return

    setLoadingMore(true)
    const nextOffset = pagination.current_offset + pagination.limit

    router.get(
      "/mi-barrio",
      {
        tab: activeTab,
        offset: nextOffset,
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["events", "businesses", "forum_posts", "pagination"],
        onFinish: () => setLoadingMore(false),
      },
    )
  }

  return (
    <AppLayout>
      <Head title={`Mi Barrio - ${neighborhood.name}`} />
      <div className="bg-background">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="p-[15px] pb-0">
            <div className="flex flex-col items-center justify-center py-4">
              <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px]">
                Mi Barrio
              </h1>
              <div className="text-muted-foreground flex items-center gap-2">
                <span className="bg-background flex h-[24px] w-[24px] flex-shrink-0 items-center justify-center rounded-full text-white">
                  <img src={EmoLocation} alt="Ubicación" />
                </span>
                <span className="text-[16px]">{neighborhood.name}</span>
              </div>
            </div>

            {/* Argentinos Counter Card */}
            <div
              className="mb-4 rounded-lg p-4"
              style={{ background: "var(--gradient-highlight)" }}
            >
              <div className="flex items-center justify-center gap-3">
                <Users className="text-primary h-5 w-5" />
                <p className="text-[16px]">
                  <span className="text-primary font-bold">
                    {argentinos_count}
                  </span>{" "}
                  {argentinos_count === 1 ? "argentino" : "argentinos"} en{" "}
                  {neighborhood.name}
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="scrollbar-hide-x w-full overflow-x-auto">
              <div className="flex flex-nowrap justify-start gap-[7px]">
                {tabs.map((tab) => (
                  <Button
                    key={tab.key}
                    variant={activeTab === tab.key ? "secondary" : "default"}
                    size="sm"
                    onClick={() => handleTabChange(tab.key)}
                    className={cn(
                      "h-auto min-w-0 flex-shrink-0 gap-[4px] rounded-[14px] px-[7px] py-[8px]",
                      activeTab === tab.key
                        ? ""
                        : "text-muted-foreground bg-card font-semibold",
                    )}
                  >
                    <span className="bg-card flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full">
                      <img src={tab.icon} alt={tab.label} />
                    </span>
                    <span className="text-[14px] tracking-[0.28px]">
                      {tab.label}
                    </span>
                    <span className="text-[12px] opacity-70">
                      ({tab.count})
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
          {/* Content Area */}
          <div className="bg-background mt-4 w-full">
            {/* Eventos Tab */}
            {activeTab === "eventos" && (
              <div>
                {allEvents.length > 0 ? (
                  <>
                    {allEvents.map((event, index) => (
                      <FeedItem
                        key={event.id}
                        item={event as unknown as FeedItemData}
                        isLast={
                          index === allEvents.length - 1 && !pagination.has_more
                        }
                      />
                    ))}
                    {pagination.has_more && (
                      <div className="p-4 text-center">
                        <Button
                          variant="ghost"
                          onClick={handleLoadMore}
                          disabled={loadingMore}
                          className="text-primary hover:bg-muted"
                        >
                          {loadingMore ? "Cargando..." : "Ver más eventos"}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                    style={{ background: "var(--gradient-highlight)" }}
                  >
                    <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                      <img src={EmoNeutral} alt="Sin contenido" />
                    </div>

                    <h3 className="text-center text-[18px] font-semibold tracking-[0px] text-balance sm:text-xl">
                      No se encontraron eventos próximos en {neighborhood.name}
                    </h3>
                  </div>
                )}
              </div>
            )}

            {/* Negocios Tab */}
            {activeTab === "negocios" && (
              <div>
                {allBusinesses.length > 0 ? (
                  <>
                    {allBusinesses.map((business, index) => (
                      <FeedItem
                        key={business.id}
                        item={business as unknown as FeedItemData}
                        isLast={
                          index === allBusinesses.length - 1 &&
                          !pagination.has_more
                        }
                      />
                    ))}
                    {pagination.has_more && (
                      <div className="p-4 text-center">
                        <Button
                          variant="ghost"
                          onClick={handleLoadMore}
                          disabled={loadingMore}
                          className="text-primary hover:bg-muted"
                        >
                          {loadingMore ? "Cargando..." : "Ver más negocios"}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                    style={{ background: "var(--gradient-highlight)" }}
                  >
                    <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                      <img src={EmoNeutral} alt="Sin contenido" />
                    </div>

                    <h3 className="text-center text-[18px] font-semibold tracking-[0px] text-balance sm:text-xl">
                      No se encontraron negocios en {neighborhood.name}
                    </h3>
                  </div>
                )}
              </div>
            )}

            {/* Foro Tab */}
            {activeTab === "foro" && (
              <div>
                {allForumPosts.length > 0 ? (
                  <>
                    {allForumPosts.map((post, index) => (
                      <FeedItem
                        key={post.id}
                        item={post as unknown as FeedItemData}
                        isLast={
                          index === allForumPosts.length - 1 &&
                          !pagination.has_more
                        }
                      />
                    ))}
                    {pagination.has_more && (
                      <div className="p-4 text-center">
                        <Button
                          variant="ghost"
                          onClick={handleLoadMore}
                          disabled={loadingMore}
                          className="text-primary hover:bg-muted"
                        >
                          {loadingMore ? "Cargando..." : "Ver más preguntas"}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                    style={{ background: "var(--gradient-highlight)" }}
                  >
                    <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                      <img src={EmoNeutral} alt="Sin contenido" />
                    </div>

                    <h3 className="text-center text-[18px] font-semibold tracking-[0px] text-balance sm:text-xl">
                      No se encontraron posts en {neighborhood.name}
                    </h3>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
